<script setup lang="ts">

import AppPage from "@/components/AppPage.vue";
import {computed, onMounted, ref} from "vue";
import {peopleBackend} from "@/backend/people.backend.ts";
import Debug from "@/components/Debug.vue";
import HOSAvatar from "@/components/hOSAvatar.vue";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import {humanDateMMMddyyyy} from "@/helpers/date.helper";
import InlineProfile from "@/components/user/InlineProfile.vue";
import {FilterMatchMode} from "@primevue/core/api";
import InputIcon from "primevue/inputicon";
import InputText from "primevue/inputtext";
import IconField from "primevue/iconfield";
import {Person} from "@/types/DataTypes.ts";
import ToggleSwitch from "primevue/toggleswitch";
import Button from "primevue/button";
import useAppToast from "@/composables/useAppToast";


const people = ref<Person[]>([]);
const expandedRows = ref<any[]>([]);
const filters = ref({global: {value: null, matchMode: FilterMatchMode.CONTAINS}})
const groupByDepartment = ref(false);
const toaster = useAppToast();

const sortedByDept = computed(() => people.value.sort((a: Person, b: Person) => departmentName(a).localeCompare(departmentName(b))))
const data = computed(() => groupByDepartment.value ? sortedByDept.value : people.value);

onMounted(async () => {
  people.value = await peopleBackend.getDirectory();
});

const departmentName = (entry: Person): string => {
  return entry.department? entry.department.name : '';
}

const copyDepartmentEmails = async (departmentData: any) => {
  const department = departmentName(departmentData);
  const departmentPeople = people.value.filter(person => 
    departmentName(person) === department && person.email
  );
  const emails = departmentPeople.map(person => person.email).join('\n');
  
  try {
    await navigator.clipboard.writeText(emails);
    toaster.success(
      `Copied ${departmentPeople.length} emails`, 
      department ? `${department} team emails copied to clipboard` : 'Team emails copied to clipboard'
    );
  } catch (err) {
    console.error('Failed to copy emails:', err);
    toaster.error('Failed to copy emails to clipboard');
  }
}
</script>

<template>
  <AppPage title="hOS Team" :loaded="true">
    <DataTable
        :value="data" selection-mode="single"
        :expanded-rows="expandedRows" data-key="id"
        :group-rows-by="groupByDepartment ? departmentName : ''"
        :rowGroupMode="groupByDepartment ? 'subheader' : ''"
        :sort-field="groupByDepartment ? '' : 'name'"
        scrollable scroll-height="75vh"
        key="id"
        :globalFilterFields="['name', 'department.name', 'meta.location', 'title']" v-model:filters="filters"
    >
      <template #header>
        <div class="flex flex-row gap-2 items-center justify-between">
          <div class="items-group">
            <IconField>
              <InputIcon><i class="pi pi-search"/></InputIcon>
              <InputText v-model="filters['global'].value" placeholder="Keyword Search"/>
            </IconField>
            <span class="text-muted-color text-sm">Press Cmd+K anywhere in the app</span>
          </div>
          <div class="items-group">
            <ToggleSwitch v-model="groupByDepartment" label="Group by department" class="ml-2"
                          inputId="groupByDepartment"/>
            <label for="groupByDepartment" class="text-muted-color">Group by function</label>
          </div>
        </div>
      </template>
      <template #groupheader="slotProps">
        <div class="flex items-center justify-between gap-2 text-lg font-semibold">
          <span>{{ departmentName(slotProps.data) }}</span>
          <Button 
            label="Copy Emails" 
            size="small" 
            severity="secondary" 
            icon="pi pi-copy"
            @click="copyDepartmentEmails(slotProps.data)"
          />
        </div>
      </template>
      <Column style="width: 1rem" expander/>
      <Column style="width: 1rem">
        <template #body="{data}">
          <h-o-s-avatar :user="data"/>
        </template>
      </Column>
      <Column field="name" header="Name" :sortable="!groupByDepartment"/>
      <Column :field="departmentName" header="Function" :sortable="!groupByDepartment"/>
      <Column field="title" header="Title" :sortable="!groupByDepartment"/>
      <Column field="meta.location" header="Location" :sortable="!groupByDepartment"/>
      <Column header="Start date" :sortable="!groupByDepartment" field="meta.startDate">
        <template #body="{data}">
          {{ humanDateMMMddyyyy(data.meta.startDate) }}
        </template>
      </Column>
      <template #expansion="{data}">
        <InlineProfile :user="data" />
      </template>
    </DataTable>
    <Debug :data="people"/>
  </AppPage>
</template>
